import { WithholdingTransactionType } from "@wingspanhq/bookkeeping/dist/lib/interfaces/withholdingTransaction";
import {
  toWSDateString,
  WSInfiniteScroll,
  WSList,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import flatten from "lodash/flatten";
import React, { useMemo } from "react";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useQueryListInternalAccountTransactions } from "../../../query/banking/queries/useQueryListInternalAccountTransactions";
import { useWithholdingTransactions } from "../../../query/taxes/queries";
import { WSQueries } from "../../../query/WSQuery";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";

export const columns: Array<WSTableColumn> = [
  {
    config: {
      header: {
        text: "Transaction"
      },
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: row => <WSTableCell text={row.data.title} />
  },
  {
    config: {
      header: {
        text: "Date"
      },
      gridTemplateSizeMax: "0.3fr"
      // justify: "end"
    },
    renderFunction: row => (
      <WSTableCell text={toWSDateString(row.data.date, "monthDayYear")} />
    )
  },
  {
    config: {
      header: {
        text: "Amount"
      },
      gridTemplateSizeMax: "0.3fr"
      // justify: "end"
    },
    renderFunction: row => (
      <WSText.ParagraphSm
        color="gray600"
        data-testid={`postedTransactionAmount_${row.id}`}
      >
        {formatMoney(row.data.amount, row.data.currency, {
          showSign: true
        })}
      </WSText.ParagraphSm>
    )
  }
];

export const TaxesTransactions: React.FC = () => {
  const queryWithholdingTransactions = useWithholdingTransactions();

  const queryInternalAccounts = useQueryInternalAccounts();
  const internalAccount = queryInternalAccounts.data?.find(
    selectorActiveTaxWithholdingAccount
  );
  const queryPendingTransactions = useQueryListInternalAccountTransactions(
    internalAccount?.internalAccountId!,
    {
      "filter[status]": "Pending"
    },
    {
      enabled: !!internalAccount
    }
  );

  const pendingTransactions = useMemo(
    () =>
      (queryPendingTransactions.data?.pages || []).flatMap(
        page => page.transactions
      ),
    [queryPendingTransactions.data?.pages]
  );

  return (
    <WSQueries
      queries={{
        queryWithholdingTransactions,
        queryPendingTransactions
      }}
    >
      {({ queryWithholdingTransactionsData }) => {
        const transactionList = flatten(
          queryWithholdingTransactionsData.pages || []
        );

        const tableDataPostedTransactions = transactionList.map(transaction => {
          return {
            data: {
              source: transaction,
              title:
                transaction.type === WithholdingTransactionType.TaxPayment
                  ? "Payment to IRS"
                  : transaction.invoiceNumber
                  ? `Set aside from Invoice #${transaction.invoiceNumber}`
                  : transaction.amount < 0
                  ? "Deposit"
                  : "Withdraw",
              description: transaction.invoiceNumber,
              date: transaction.createdAt,
              amount: transaction.amount * -1
            },
            id: transaction.withholdingTransactionId
          };
        });

        const tableDataPendingTransactions = (pendingTransactions || []).map(
          transaction => {
            return {
              data: {
                source: transaction,
                isPending: true,
                amount: transaction.amount,
                // TODO: update title once BE returns correct type
                title: transaction.amount > 0 ? "Deposit" : "Withdraw",
                date: transaction.createdAt
              },
              id: transaction.transactionId
            };
          }
        );

        if (
          tableDataPendingTransactions.length === 0 &&
          tableDataPostedTransactions.length === 0
        ) {
          return null;
        }

        return (
          <WSList gap="XL">
            <WSText.Paragraph weight="medium">Transactions</WSText.Paragraph>

            <WSTable
              tableData={tableDataPendingTransactions}
              columns={columns}
              showHeader
              forceShowGroups
              getGroupName={() => "Pending"}
            />

            <WSInfiniteScroll
              onLoad={() => {
                queryWithholdingTransactions.fetchNextPage();
              }}
              loadMore={transactionList.length > 0}
              endOfList={!queryWithholdingTransactions.hasNextPage}
              loading={!!queryWithholdingTransactions.isFetchingNextPage}
            >
              <WSTable
                tableData={tableDataPostedTransactions}
                columns={columns}
                showHeader={!tableDataPendingTransactions.length}
                forceShowGroups
                getGroupName={() => "Posted"}
                bottomMessage={
                  !tableDataPendingTransactions.length &&
                  !tableDataPostedTransactions.length
                    ? "No activity yet"
                    : undefined
                }
              />
            </WSInfiniteScroll>
          </WSList>
        );
      }}
    </WSQueries>
  );
};
